import { useEffect, useState } from "react";
import { Loader2, ChevronDown, Clock } from "lucide-react";
import { kioskMyHours } from "../../lib/teamsSchedule";
import { fmtClock, fmtHours, fmtDay } from "./kioskUtils";

const DayRow = ({ day }) => (
  <div className="py-2 border-t border-neutral-800 first:border-t-0" data-testid={`kiosk-hours-day-${day.date}`}>
    <div className="flex justify-between text-sm font-semibold">
      <span>{fmtDay(day.date)}</span>
      <span className="font-mono">{fmtHours(day.total_hours)}</span>
    </div>
    {day.entries.map((en, i) => (
      <div key={i} className="flex flex-wrap items-center gap-x-3 text-xs text-neutral-400 mt-1">
        <span>{fmtClock(en.clock_in_at)} – {en.still_open ? "now" : fmtClock(en.clock_out_at)}</span>
        {en.break_hours > 0 && <span>Break {fmtHours(en.break_hours)}</span>}
        {en.still_open && <span className="text-green-400">In progress</span>}
        {en.auto_clocked_out && <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">Auto clocked-out</span>}
      </div>
    ))}
  </div>
);

const WeekBlock = ({ week, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-neutral-800 rounded-lg" data-testid={`kiosk-hours-week-${week.week_start}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-800/60"
        data-testid={`kiosk-hours-week-toggle-${week.week_start}`}>
        <div className="text-left">
          <div className="font-semibold">{week.label}</div>
          <div className="text-xs text-neutral-500">{fmtDay(week.week_start)} – {fmtDay(week.week_end)}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono font-bold text-yellow-400" data-testid={`kiosk-hours-week-total-${week.week_start}`}>{fmtHours(week.total_hours)}</span>
          <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3">
          {week.days.length === 0 ? <div className="text-xs text-neutral-500 py-2">No hours logged.</div>
            : week.days.map((d) => <DayRow key={d.date} day={d} />)}
        </div>
      )}
    </div>
  );
};

export const MyHoursPanel = ({ employeeId, passcode, refreshKey }) => {
  const [weeks, setWeeks] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    kioskMyHours({ employee_id: employeeId, passcode, weeks: 3 })
      .then((r) => { setErr(""); setWeeks([...(r.weeks || [])].reverse()); })
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load hours"));
  }, [employeeId, passcode, refreshKey]);

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 mt-6" data-testid="kiosk-my-hours">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-neutral-400 mb-3">
        <Clock size={12} /> My Hours
      </div>
      {err && <div className="text-red-400 text-sm">{err}</div>}
      {!weeks && !err && <Loader2 size={18} className="animate-spin" />}
      <div className="space-y-2">
        {weeks?.map((w, i) => <WeekBlock key={w.week_start} week={w} defaultOpen={i === 0} />)}
      </div>
    </div>
  );
};
